import React from 'react';
import { useI18nContext } from '@procore/core-react';
import Head from 'next/head';
import { Box } from '@mui/material';
import { useRouter } from 'next/router';
import { type GetServerSidePropsContext } from 'next';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import routes from '../../helpers/routes';
import StatePageContent from '../../components/Layout/StatePageContent';
import { canonicalUrl } from '../../helpers/canonical-url';
import { FilterUrlKey } from '../../helpers/filters';
import { useStyles } from './[trade].styles';
import { usePageView } from '../../components/Analytics/utilities';
import { requestTrades } from '../../services/business-registry';
import { findTradeOrSpecialty } from '../../components/utils/tradesPageUtil';
import { iterableProvidedServices } from '../../data/filters/provided-services';

const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { trade } = context.query;
  const tradeData = iterableProvidedServices.find((ps) => ps.urlKey === trade);
  if (tradeData) {
    return { props: { trade: tradeData } };
  }

  const tradesList = await requestTrades(context.locale);
  const providedService = findTradeOrSpecialty(trade, tradesList);

  if (providedService) {
    return {
      props: {
        trade: {
          name: providedService.label,
          urlKey: providedService.urlKey,
        },
      },
    };
  }

  return {
    notFound: true,
  };
};

export { getServerSideProps };

export default function TradePage({ trade }) {
  const I18n = useI18nContext();
  const classes = useStyles();
  const router = useRouter();
  const canonicalURL = canonicalUrl(router.asPath);
  usePageView(`View Trade - ${trade.name}`);

  return (
    <>
      <Head>
        <title>
          {I18n.t('pages.trades.trade.title', {
            name: trade.name,
          })}
        </title>
        <meta
          name="description"
          content={`Search, find and connect with ${trade.name} contractors on Procore. Quickly find thousands of construction professionals on Procore ready to build.`}
        />
        <link rel="canonical" key="canonical" href={canonicalURL} />
      </Head>
      <Box className={classes.root}>
        <main>
          <Header />
          <StatePageContent
            hero={{
              title: I18n.t('pages.trades.trade.hero.title', {
                name: trade.name,
              }),
              subTitle: I18n.t('pages.trades.trade.hero.sub-title'),
            }}
            item={trade}
            itemType={FilterUrlKey.trade}
            getStateLink={(tradeParameter, country, state) =>
              routes.trades(tradeParameter, country, state)
            }
          />
        </main>
        <Footer />
      </Box>
    </>
  );
}
